
import { mapState, mapGetters, mapActions } from 'vuex';
import { TRANSPARENT_IMAGE_URL } from '@/constants/IMAGE_URLS';
import {
  TAP_DELAY,
  THUMBNAIL_WIDTH,
  SWIPE_THRESHOLD,
  VIDEO_INDICATOR_ANIMATION_DURATION,
  REVIEW_DETAIL_DIALOG_HEIGHT
} from '@/constants/SHORTS_POPUP';
import ReviewInfo from './ReviewInfo';
import ActionButtons from './ActionButtons';
import MediaOrientation from '@/enums/MediaOrientation';
import pinchZoom from '@/mixins/pinchZoom';

export default {
  i18nComponentKey: 'ReviewsShortsPopup.components.ReviewShortsMedium',
  name: 'ReviewShortsMedium',
  components: { ReviewInfo, ActionButtons },
  mixins: [pinchZoom],
  props: {
    review: {
      type: Object,
      required: true
    },
    mediaPosition: {
      type: Number,
      default: null,
      validator: v => v === null || [1, 2, 3, 4].includes(v)
    },
    reviewIndex: {
      type: Number,
      default: null
    },
    isVideoMuted: {
      type: Boolean,
      default: true
    },
    popupParams: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      videoIndicator: {
        rendered: false,
        key: 0,
        name: 'icon-play'
      },
      lastTapTime: null,
      singleTapTimeout: null,
      playPauseObserver: null,
      canPlayThroughHandler: null,
      currentTime: 0,
      duration: 0,
      isDragging: false,
      dragTime: 0,
      touchStartY: null,
      isSwiped: false,
      isBottomSheetInTransition: false,
      userPaused: false
    };
  },
  computed: {
    ...mapState('bottomSheet', ['bottomSheet', 'bottomSheetHeight']),
    ...mapGetters('widget', ['isPcViewport']),
    image() {
      const imageIdx = this.mediaPosition
        ? this.mediaPosition - this.review.videos.length - 1
        : 0;
      return this.review.images[imageIdx];
    },
    imageSrc() {
      return this.image?.url || TRANSPARENT_IMAGE_URL;
    },
    video() {
      const videoIdx = this.mediaPosition ? this.mediaPosition - 1 : 0;
      return this.review.videos[videoIdx];
    },
    videoSrc() {
      return this.video?.url;
    },
    mediaOrientation() {
      const orientation = this.video?.orientation || this.image?.orientation;

      return MediaOrientation.key(orientation || MediaOrientation.NONE);
    },
    imageShape() {
      // HACK: iOS 18.x Safari에서 바텀시트 열림 시 vertical 이미지가 축소되지 않는 현상 우회 (iOS 26.x에서는 정상 동작)
      if (this.isReviewDetailBottomSheetOpen) return 'original';
      return this.mediaOrientation === 'PORTRAIT' ? 'vertical' : 'original';
    },
    dragPercentage() {
      if (!this.duration) return 0;

      const percentage = (this.dragTime / this.duration) * 100;
      if (!this.$el || this.$el.offsetWidth === 0) {
        return percentage;
      }

      const edgeOffsetPercent =
        (THUMBNAIL_WIDTH / 2 / this.$el.offsetWidth) * 100;
      const minPositionPercent = edgeOffsetPercent;
      const maxPositionPercent = 100 - edgeOffsetPercent;
      return Math.max(
        minPositionPercent,
        Math.min(maxPositionPercent, percentage)
      );
    },
    formattedDragTime() {
      const minutes = Math.floor(this.dragTime / 60);
      const seconds = Math.floor(this.dragTime % 60);
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    },
    mediaWrapperStyle() {
      const height = this.bottomSheetHeight
        ? `calc(100dvh - ${this.bottomSheetHeight}px)`
        : '100%';

      const videoObjectFit =
        ['LANDSCAPE', 'SQUARE'].includes(this.mediaOrientation) ||
        this.isReviewDetailBottomSheetOpen
          ? 'contain'
          : 'cover';

      return {
        '--media-wrapper-height': height,
        '--video-indicator-animation-duration': `${VIDEO_INDICATOR_ANIMATION_DURATION}ms`,
        '--pinch-zoom-touch-action': this.pinchZoomTouchAction,
        '--pinch-zoom-transform': this.pinchZoomTransform,
        '--video-object-fit': videoObjectFit
      };
    },
    isReviewDetailBottomSheetOpen() {
      return !!this.bottomSheetHeight && this.bottomSheetHeight > 0;
    },
    isPinchZooming() {
      return this.zoomScale > 1 || this.activeTouches.length >= 2;
    },
    bottomSheetWrapperStyle() {
      return {
        '--bottom-sheet-pointer-events': this.bottomSheet ? 'auto' : 'none'
      };
    }
  },
  watch: {
    isPinchZooming(newValue) {
      this.$emit('pinch-zoom-change', newValue);

      if (newValue) {
        this.disconnectPlayPauseObserver();
      } else {
        this.reconnectPlayPauseObserver();
      }
    },
    isVideoMuted(newValue) {
      const video = this.$refs.video;
      if (!video) return;

      video.muted = newValue;
    },
    bottomSheetHeight(newValue, oldValue) {
      this.isBottomSheetInTransition =
        (!oldValue && newValue === REVIEW_DETAIL_DIALOG_HEIGHT) ||
        (oldValue && newValue === null);
    }
  },
  mounted() {
    this.setIntersectionObserver();
  },
  beforeDestroy() {
    this.clearSingleTapTimeout();
    this.playPauseObserver?.disconnect();
    if (this.canPlayThroughHandler) {
      this.$refs.video?.removeEventListener(
        'canplaythrough',
        this.canPlayThroughHandler
      );
      this.canPlayThroughHandler = null;
    }
  },
  methods: {
    ...mapActions('like', ['toggleLike']),
    pointerupHandler(ev) {
      this.removeActiveTouch(ev);

      if (this.activeTouches.length < 2) {
        this.prevDiff = -1;
      }

      if (this.activeTouches.length === 0) {
        this.resetTransform();
      }
    },
    onTouchStart(event) {
      if (event.touches.length >= 2) return;

      const touch = event.touches[0];
      this.touchStartY = touch.clientY;
      this.isSwiped = false;
    },
    onTouchMove(event) {
      if (event.touches.length >= 2) {
        this.touchStartY = null;
        return;
      }

      if (this.isSwiped || this.touchStartY === null) return;

      const touch = event.touches[0];
      const deltaY = Math.abs(touch.clientY - this.touchStartY);

      if (deltaY > SWIPE_THRESHOLD) {
        this.isSwiped = true;
      }
    },
    onTouchEnd(e) {
      this.clearSingleTapTimeout();

      if (this.isSwiped || this.touchStartY === null) {
        this.touchStartY = null;
        this.isSwiped = false;
        return;
      }

      e.preventDefault();

      const currentTime = Date.now();
      const isDoubleTap = this.lastTapTime
        ? currentTime - this.lastTapTime < TAP_DELAY
        : false;
      if (isDoubleTap) {
        this.toggleLike({ review: this.review });
      } else {
        this.singleTapTimeout = setTimeout(() => {
          this.togglePlayPause();
        }, TAP_DELAY);
      }
      this.lastTapTime = currentTime;
      this.touchStartY = null;
      this.isSwiped = false;
    },
    playVideo() {
      const video = this.$refs.video;
      if (!video) return;

      video.muted = this.isVideoMuted;
      video.play().catch(() => {});
    },
    resetVideo() {
      const video = this.$refs.video;
      if (!video) return;

      this.userPaused = true;
      video.pause();
      video.currentTime = 0;
    },
    togglePlayPause() {
      const video = this.$refs.video;
      if (!video) return;

      const wasPaused = video.paused;
      wasPaused ? this.playVideo() : video.pause();
      this.userPaused = !wasPaused;
      this.videoIndicator.rendered = true;
      this.videoIndicator.key++;
      this.videoIndicator.name = wasPaused ? 'icon-play' : 'icon-pause';
    },
    clearSingleTapTimeout() {
      if (this.singleTapTimeout) {
        clearTimeout(this.singleTapTimeout);
        this.singleTapTimeout = null;
      }
    },
    setIntersectionObserver() {
      const video = this.$refs.video;
      if (!video) return;

      this.playPauseObserver = new IntersectionObserver(
        this.onIntersectForPlayPause,
        {
          root: null,
          threshold: 0.5
        }
      );

      if (video.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA) {
        this.playPauseObserver.observe(video);
      } else {
        this.waitForCanPlayThrough(video);
      }
    },
    disconnectPlayPauseObserver() {
      this.playPauseObserver?.disconnect();
    },
    reconnectPlayPauseObserver() {
      const video = this.$refs.video;
      if (!video || !this.playPauseObserver) return;

      this.playPauseObserver.observe(video);
    },
    waitForCanPlayThrough(video) {
      const playPauseObserver = this.playPauseObserver;
      this.canPlayThroughHandler = () => {
        if (video.readyState === HTMLMediaElement.HAVE_ENOUGH_DATA) {
          playPauseObserver.observe(video);
          video.removeEventListener(
            'canplaythrough',
            this.canPlayThroughHandler
          );
          this.canPlayThroughHandler = null;
        }
      };
      video.addEventListener('canplaythrough', this.canPlayThroughHandler);
    },
    onIntersectForPlayPause(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.userPaused = false;
          this.playVideo();
        } else {
          this.resetVideo();
        }
      });
    },
    onTimeUpdate() {
      if (this.isDragging) return;

      const video = this.$refs.video;
      if (!video) return;

      this.currentTime = video.currentTime;
    },
    onLoadedMetadata() {
      const video = this.$refs.video;
      if (!video) return;

      this.duration = video.duration;
    },
    onDragStart(time) {
      this.isDragging = true;
      this.dragTime = time;
      this.updateThumbnail();
    },
    onDragMove(time) {
      this.dragTime = time;
      this.updateThumbnail();
    },
    onDragEnd(time) {
      this.isDragging = false;
      const video = this.$refs.video;
      if (!video) return;

      video.currentTime = time;
      this.currentTime = time;
    },
    resumeVideoIfVisible() {
      const video = this.$refs.video;
      if (!video) return;

      const rect = video.getBoundingClientRect();
      const isVisible =
        rect.top < window.innerHeight &&
        rect.bottom > 0 &&
        rect.left < window.innerWidth &&
        rect.right > 0;
      if (isVisible) {
        video.play().catch(() => {});
      }
    },
    onPause() {
      if (this.userPaused) return;
      this.resumeVideoIfVisible();
    },
    updateThumbnail() {
      const thumbnailVideo = this.$refs.thumbnailVideo;
      if (!thumbnailVideo || !this.duration || !this.videoSrc) return;

      if (thumbnailVideo.readyState >= HTMLMediaElement.HAVE_METADATA) {
        thumbnailVideo.play().then(() => {
          thumbnailVideo.pause();
          thumbnailVideo.currentTime = this.dragTime;
        });
      } else {
        thumbnailVideo.addEventListener(
          'loadedmetadata',
          () => {
            thumbnailVideo.currentTime = this.dragTime;
          },
          { once: true }
        );
        thumbnailVideo.load();
      }
    }
  }
};
